import React, { useRef } from 'react';
import { useDashboardContext } from '@pages/DashboardLayout';
import QRCode from 'react-qr-code';
import { toast } from 'react-toastify';

const QRCodeDownload = () => {
  const { user } = useDashboardContext();
  const qrCodeRef = useRef(null);
  const profileUrl = `https://app.svisit.ch/c/${user.url}`;

  const handleDownloadSVG = () => {
    try {
      const svg = qrCodeRef.current.querySelector('svg');
      if (!svg) {
        toast.error('QR-Code konnte nicht gefunden werden');
        return;
      }

      // Clone the SVG to avoid modifying the original
      const svgClone = svg.cloneNode(true);

      // Add XML namespace if not present
      svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

      // Serialize SVG to string
      const svgData = new XMLSerializer().serializeToString(svgClone);
      const svgBlob = new Blob([svgData], {
        type: 'image/svg+xml;charset=utf-8',
      });

      // Create download link
      const url = URL.createObjectURL(svgBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `svisit_qr_code_${user.url}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('QR-Code als SVG heruntergeladen');
    } catch (error) {
      console.error('Error downloading SVG:', error);
      toast.error('Fehler beim Herunterladen des QR-Codes');
    }
  };

  const handleDownloadPNG = () => {
    try {
      const svg = qrCodeRef.current.querySelector('svg');
      if (!svg) {
        toast.error('QR-Code konnte nicht gefunden werden');
        return;
      }

      // Get SVG data
      const svgClone = svg.cloneNode(true);
      svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      const svgData = new XMLSerializer().serializeToString(svgClone);

      // Create canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      // Set canvas size (2x for better quality)
      const size = 1024;
      canvas.width = size;
      canvas.height = size;

      img.onload = () => {
        // Fill white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);

        // Draw QR code
        ctx.drawImage(img, 0, 0, size, size);

        // Convert to blob and download
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `svisit_qr_code_${user.url}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          toast.success('QR-Code als PNG heruntergeladen');
        }, 'image/png');
      };

      img.onerror = (error) => {
        console.error('Error loading image:', error);
        toast.error('Fehler beim Konvertieren des QR-Codes');
      };

      // Load SVG into image
      const svgBlob = new Blob([svgData], {
        type: 'image/svg+xml;charset=utf-8',
      });
      const url = URL.createObjectURL(svgBlob);
      img.src = url;
    } catch (error) {
      console.error('Error downloading PNG:', error);
      toast.error('Fehler beim Herunterladen des QR-Codes');
    }
  };

  return (
    <div className="bg-gray-50 px-6 py-8 sm:py-10 lg:px-8">
      <div className="mx-auto max-w-2xl">
        <h3 className="text-lg font-semibold leading-7 text-gray-900 mb-6">
          QR-Code Download
        </h3>
        <p className="text-sm text-gray-600 mb-8">
          Lade deinen persönlichen QR-Code als SVG oder PNG herunter.
        </p>

        <div className="bg-white rounded-lg border border-gray-300 p-8">
          <div className="flex flex-col items-center">
            {/* QR Code Display */}
            <div
              ref={qrCodeRef}
              className="mb-6 p-4 bg-white rounded-lg border-2 border-gray-200"
            >
              <QRCode value={profileUrl} size={256} level="H" />
            </div>

            {/* Profile URL */}
            <p className="text-sm text-gray-500 mb-6 text-center break-all">
              {profileUrl}
            </p>

            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <button
                onClick={handleDownloadSVG}
                className="inline-flex justify-center items-center rounded-md bg-svisit-primary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus:outline-none focus:ring-2 focus:ring-svisit-primary-500 focus:ring-offset-2 hover:cursor-pointer"
              >
                <svg
                  className="mr-2 h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Als SVG herunterladen
              </button>

              <button
                onClick={handleDownloadPNG}
                className="inline-flex justify-center items-center rounded-md bg-white px-6 py-2.5 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-svisit-primary-500 focus:ring-offset-2 hover:cursor-pointer"
              >
                <svg
                  className="mr-2 h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Als PNG herunterladen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeDownload;
